(() => {
  if (window.MafraArabicSearchSuggestionsLoaded) return;
  window.MafraArabicSearchSuggestionsLoaded = true;

  const isArabicStorefront =
    document.documentElement.dir === 'rtl' ||
    String(document.documentElement.lang || '').toLowerCase().startsWith('ar');

  if (!isArabicStorefront) return;

  const MAX_RESULTS = 5;
  const MIN_QUERY_LENGTH = 2;
  const FALLBACK_DELAY = 650;
  const resultCache = new Map();
  let instanceCounter = 0;

  const normaliseText = (value) =>
    String(value || '')
      .replace(/\s+/g, ' ')
      .trim();

  const getLocaleSearchUrl = (query) => {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}search`, window.location.origin);

    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');

    return url;
  };

  const extractProductsFromSearchPage = (html) => {
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const grid = parsedDocument.querySelector('#product-grid');
    if (!grid) return [];

    const products = [];
    const seenUrls = new Set();

    grid.querySelectorAll(':scope > .grid__item, .grid__item').forEach((item) => {
      if (products.length >= MAX_RESULTS) return;

      const link =
        item.querySelector('.mafra-product-card__title a[href]') ||
        item.querySelector('.card__heading a[href]') ||
        item.querySelector('a.full-unstyled-link[href*="/products/"]') ||
        item.querySelector('a[href*="/products/"]');

      if (!link) return;

      const title = normaliseText(link.textContent);
      const href = link.getAttribute('href');
      if (!title || !href) return;

      const absoluteUrl = new URL(href, window.location.origin);
      if (seenUrls.has(absoluteUrl.href)) return;
      seenUrls.add(absoluteUrl.href);

      const image = item.querySelector('img');
      const imageUrl = image
        ? image.getAttribute('src') ||
          image.getAttribute('data-src') ||
          image.getAttribute('srcset')?.split(',')[0]?.trim()?.split(' ')[0] ||
          ''
        : '';

      products.push({
        title,
        url: absoluteUrl.pathname + absoluteUrl.search,
        image: imageUrl ? new URL(imageUrl, window.location.origin).href : ''
      });
    });

    return products;
  };

  const extractNativeProducts = (predictiveSearch) => {
    const products = [];
    const seenUrls = new Set();

    predictiveSearch
      .querySelectorAll('#predictive-search-results-products-list a[href]')
      .forEach((link) => {
        if (products.length >= MAX_RESULTS) return;

        const titleElement = link.querySelector('.predictive-search__item-heading');
        const title = normaliseText(titleElement?.textContent || link.textContent);
        const href = link.getAttribute('href');

        if (!title || !href) return;

        const absoluteUrl = new URL(href, window.location.origin);
        if (seenUrls.has(absoluteUrl.href)) return;
        seenUrls.add(absoluteUrl.href);

        const image = link.querySelector('img');
        products.push({
          title,
          url: absoluteUrl.pathname + absoluteUrl.search,
          image: image?.currentSrc || image?.getAttribute('src') || ''
        });
      });

    return products;
  };

  const hasNativeQuerySuggestions = (predictiveSearch) =>
    Boolean(
      predictiveSearch.querySelector(
        '#predictive-search-results-queries-list .predictive-search__list-item'
      )
    );

  const removeFallback = (predictiveSearch) => {
    predictiveSearch
      .querySelectorAll('[data-mafra-arabic-search-fallback]')
      .forEach((element) => element.remove());
  };

  const createSuggestionBlock = (instanceId, products) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'predictive-search__result-group';
    wrapper.setAttribute('data-mafra-arabic-search-fallback', '');
    wrapper.setAttribute('dir', 'rtl');

    const inner = document.createElement('div');

    const heading = document.createElement('h2');
    heading.id = `mafra-arabic-search-suggestions-${instanceId}`;
    heading.className =
      'predictive-search__heading text-body caption-with-letter-spacing';
    heading.textContent = 'اقتراحات البحث';

    const list = document.createElement('ul');
    list.className = 'predictive-search__results-list list-unstyled';
    list.setAttribute('role', 'group');
    list.setAttribute('aria-labelledby', heading.id);

    products.forEach((product, index) => {
      const item = document.createElement('li');
      item.id = `mafra-arabic-search-option-${instanceId}-${index + 1}`;
      item.className = 'predictive-search__list-item';
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', 'false');

      const link = document.createElement('a');
      link.href = product.url;
      link.className =
        'predictive-search__item predictive-search__item--link-with-thumbnail link link--text';
      link.setAttribute('tabindex', '-1');

      if (product.image) {
        const image = document.createElement('img');
        image.className = 'predictive-search__image';
        image.src = product.image;
        image.alt = '';
        image.width = 50;
        image.height = 50;
        image.loading = 'lazy';
        link.appendChild(image);
      }

      const content = document.createElement('div');
      content.className =
        'predictive-search__item-content predictive-search__item-content--centered';

      const title = document.createElement('p');
      title.className = 'predictive-search__item-heading h5';
      title.textContent = product.title;
      title.setAttribute('dir', 'rtl');

      content.appendChild(title);
      link.appendChild(content);
      item.appendChild(link);
      list.appendChild(item);
    });

    inner.appendChild(heading);
    inner.appendChild(list);
    wrapper.appendChild(inner);

    return wrapper;
  };

  const createCompleteFallbackResults = (instanceId, query, products) => {
    const results = document.createElement('div');
    results.id = 'predictive-search-results';
    results.setAttribute('role', 'listbox');
    results.setAttribute('data-mafra-arabic-search-fallback', '');
    results.setAttribute('dir', 'rtl');

    const groupsWrapper = document.createElement('div');
    groupsWrapper.id = 'predictive-search-results-groups-wrapper';
    groupsWrapper.className =
      'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';

    groupsWrapper.appendChild(createSuggestionBlock(instanceId, products));
    results.appendChild(groupsWrapper);

    const searchButtonWrapper = document.createElement('div');
    searchButtonWrapper.id = `mafra-arabic-search-for-${instanceId}`;
    searchButtonWrapper.className = 'predictive-search__search-for-button';

    const searchButton = document.createElement('button');
    searchButton.type = 'submit';
    searchButton.className =
      'predictive-search__item predictive-search__item--term link link--text h5 animate-arrow';
    searchButton.setAttribute('tabindex', '-1');
    searchButton.setAttribute('role', 'option');
    searchButton.setAttribute('aria-selected', 'false');
    searchButton.textContent = `البحث عن “${query}”`;

    searchButtonWrapper.appendChild(searchButton);
    results.appendChild(searchButtonWrapper);

    const liveRegion = document.createElement('span');
    liveRegion.className = 'hidden';
    liveRegion.setAttribute('data-predictive-search-live-region-count-value', '');
    liveRegion.textContent = `${products.length} نتائج`;

    results.appendChild(liveRegion);
    return results;
  };

  const insertSuggestionsIntoNativeResults = (
    predictiveSearch,
    instanceId,
    products
  ) => {
    removeFallback(predictiveSearch);

    const groupsWrapper = predictiveSearch.querySelector(
      '#predictive-search-results-groups-wrapper'
    );
    if (!groupsWrapper) return false;

    const firstGroup = groupsWrapper.querySelector(
      '.predictive-search__result-group'
    );

    const suggestionBlock = createSuggestionBlock(instanceId, products);

    if (firstGroup) {
      firstGroup.insertBefore(
        suggestionBlock.firstElementChild,
        firstGroup.firstChild
      );
      firstGroup.firstElementChild?.setAttribute(
        'data-mafra-arabic-search-fallback',
        ''
      );
    } else {
      groupsWrapper.prepend(suggestionBlock);
    }

    return true;
  };

  const openPredictiveSearch = (predictiveSearch, productCount) => {
    predictiveSearch.setAttribute('results', 'true');

    const input = predictiveSearch.querySelector('input[type="search"]');
    if (input) input.setAttribute('aria-expanded', 'true');

    if (typeof predictiveSearch.open === 'function') {
      predictiveSearch.open();
    } else {
      predictiveSearch.setAttribute('open', 'true');
    }

    const status = predictiveSearch.querySelector('.predictive-search-status');
    if (status) {
      status.textContent = `${productCount} نتائج`;
      status.setAttribute('aria-hidden', 'false');
      window.setTimeout(() => status.setAttribute('aria-hidden', 'true'), 1000);
    }
  };

  const fetchNormalSearchProducts = async (
    query,
    abortController
  ) => {
    if (resultCache.has(query)) return resultCache.get(query);

    const response = await fetch(getLocaleSearchUrl(query).toString(), {
      signal: abortController.signal,
      headers: {
        Accept: 'text/html'
      }
    });

    if (!response.ok) {
      throw new Error(`Arabic search fallback failed: ${response.status}`);
    }

    const products = extractProductsFromSearchPage(await response.text());
    resultCache.set(query, products);
    return products;
  };

  const attachFallback = (predictiveSearch) => {
    if (predictiveSearch.dataset.mafraArabicSearchReady === 'true') return;
    predictiveSearch.dataset.mafraArabicSearchReady = 'true';

    const input = predictiveSearch.querySelector('input[type="search"]');
    const resultsContainer = predictiveSearch.querySelector(
      '[data-predictive-search]'
    );

    if (!input || !resultsContainer) return;

    instanceCounter += 1;
    const instanceId = instanceCounter;
    let timer = null;
    let abortController = null;
    let isRendering = false;

    const handleNativeResults = () => {
      if (isRendering || hasNativeQuerySuggestions(predictiveSearch)) return;

      const query = normaliseText(input.value);
      if (query.length < MIN_QUERY_LENGTH) return;

      const nativeProducts = extractNativeProducts(predictiveSearch);
      if (!nativeProducts.length) return;

      isRendering = true;
      insertSuggestionsIntoNativeResults(
        predictiveSearch,
        instanceId,
        nativeProducts
      );
      isRendering = false;
    };

    const ensureArabicSuggestions = async () => {
      const query = normaliseText(input.value);

      if (query.length < MIN_QUERY_LENGTH) {
        removeFallback(predictiveSearch);
        return;
      }

      if (hasNativeQuerySuggestions(predictiveSearch)) return;

      const nativeProducts = extractNativeProducts(predictiveSearch);
      if (nativeProducts.length) {
        isRendering = true;
        insertSuggestionsIntoNativeResults(
          predictiveSearch,
          instanceId,
          nativeProducts
        );
        isRendering = false;
        return;
      }

      abortController?.abort();
      abortController = new AbortController();

      try {
        const products = await fetchNormalSearchProducts(
          query,
          abortController
        );

        if (
          abortController.signal.aborted ||
          normaliseText(input.value) !== query ||
          !products.length
        ) {
          return;
        }

        isRendering = true;
        resultsContainer.replaceChildren(
          createCompleteFallbackResults(instanceId, query, products)
        );
        isRendering = false;

        openPredictiveSearch(predictiveSearch, products.length);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn(error);
        }
      }
    };

    const scheduleFallback = () => {
      window.clearTimeout(timer);
      abortController?.abort();
      removeFallback(predictiveSearch);

      const query = normaliseText(input.value);
      if (query.length < MIN_QUERY_LENGTH) return;

      timer = window.setTimeout(ensureArabicSuggestions, FALLBACK_DELAY);
    };

    input.addEventListener('input', scheduleFallback);
    input.addEventListener('focus', scheduleFallback);

    const observer = new MutationObserver(() => {
      if (isRendering) return;
      window.requestAnimationFrame(handleNativeResults);
    });

    observer.observe(resultsContainer, {
      childList: true,
      subtree: true
    });
  };

  const initialise = () => {
    document.querySelectorAll('predictive-search').forEach(attachFallback);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }

  const pageObserver = new MutationObserver(initialise);
  pageObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
